export const JpegMimeType = "image/jpeg";

export type JpegConversionFailure = "decodeFailed" | "noCanvasContext" | "notJpegOutput";

export class JpegConversionError extends Error {
    constructor(readonly reason: JpegConversionFailure) {
        super(`JPEG conversion failed: ${ reason }`);
        // Extending Error under an ES5 target breaks instanceof unless the prototype is restored.
        Object.setPrototypeOf(this, JpegConversionError.prototype);
        this.name = "JpegConversionError";
    }
}

/**
 * Converts a base64-encoded image to JPEG format using the Canvas API.
 * Resizes to fit within maxDimension on the long side. Rejects with a JpegConversionError
 * when the image cannot be decoded or the canvas cannot produce JPEG output.
 */
export function convertToJpegStrict(base64: string, maxDimension: number, quality = 0.85): Promise<string> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            let canvasWidth: number;
            let canvasHeight: number;
            if (img.width > maxDimension || img.height > maxDimension) {
                if (img.width >= img.height) {
                    canvasWidth = maxDimension;
                    canvasHeight = Math.round(img.height * maxDimension / img.width);
                } else {
                    canvasHeight = maxDimension;
                    canvasWidth = Math.round(img.width * maxDimension / img.height);
                }
            } else {
                canvasWidth = img.width;
                canvasHeight = img.height;
            }
            const canvas = document.createElement("canvas");
            canvas.width = canvasWidth;
            canvas.height = canvasHeight;
            const ctx = canvas.getContext("2d");
            if (!ctx) {
                reject(new JpegConversionError("noCanvasContext"));
                return;
            }
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            const result = canvas.toDataURL(JpegMimeType, quality);
            // Release GPU-backed canvas memory before GC — helpful on low-memory devices.
            img.src = "";
            canvas.width = 0;
            // toDataURL returns "data:," instead of throwing when the canvas cannot be encoded.
            if (!result.startsWith(`data:${ JpegMimeType }`)) {
                reject(new JpegConversionError("notJpegOutput"));
                return;
            }
            resolve(result);
        };
        img.onerror = () => {
            reject(new JpegConversionError("decodeFailed"));
        };
        // NativeSDK readAsDataURL may return application/octet-stream; re-wrap as image/png so WebView onload fires.
        const base64Separator = base64.indexOf("base64,");
        const rawBase64 = base64Separator >= 0 ? base64.substring(base64Separator + 7) : base64;
        const imgSrc = base64.startsWith("data:image/") ? base64 : `data:image/png;base64,${ rawBase64 }`;
        img.src = imgSrc;
    });
}

/**
 * convertToJpegStrict, falling back to the original base64 when conversion fails.
 * Only for callers that choose the upload format from the returned data; an upload
 * against an already-registered .jpg MediaFile must use convertToJpegStrict.
 */
export function convertToJpeg(base64: string, maxDimension: number, quality = 0.85): Promise<string> {
    return convertToJpegStrict(base64, maxDimension, quality).catch(() => base64);
}
